import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import type { FavoriteRadioEntry } from '@/mocks/listenerCenter';

interface Props {
  radios: FavoriteRadioEntry[];
}

export default function FavoriteRadios({ radios }: Props) {
  const navigate = useNavigate();
  const [items, setItems] = useState(radios);

  const confirmRemove = (id: number, name: string) => {
    Swal.fire({
      title: 'Quitar de favoritos',
      text: `¿Querés eliminar ${name} de tus favoritas?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, quitar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setItems(items.filter((r) => r.id !== id));
        Swal.fire({
          toast: true,
          position: 'top-end',
          title: 'Radio eliminada de favoritos',
          icon: 'success',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
          customClass: { popup: 'rounded-xl', title: 'text-sm font-medium' },
          buttonsStyling: false,
        });
      }
    });
  };

  const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground-950">Radios Favoritas ({items.length})</h3>
      </div>
      {items.length === 0 ? (
        <div className="bg-background-50 rounded-2xl p-10 text-center">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-rose-100 flex items-center justify-center">
            <i className="ri-heart-line text-2xl text-rose-400"></i>
          </div>
          <p className="text-foreground-600 text-sm">No tienes radios favoritas aún</p>
          <p className="text-foreground-400 text-xs mt-1">Marca una radio como favorita para verla aquí</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((radio) => (
            <div
              key={radio.id}
              className="bg-background-50 rounded-xl p-4 flex items-center gap-3 group hover:bg-background-100/80 transition-all cursor-pointer"
              onClick={() => navigate(`/radio/${slug(radio.stationName)}`)}
            >
              <div className="relative shrink-0">
                <img
                  src={radio.logoUrl}
                  alt={radio.stationName}
                  className="w-12 h-12 rounded-xl object-cover object-top"
                />
                {radio.isLive && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-accent-500 rounded-full border-2 border-background-50 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-background-50 rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-foreground-950 truncate">{radio.stationName}</h4>
                <div className="flex items-center gap-2 text-xs text-foreground-500 mt-0.5">
                  <span>{radio.frequency} FM</span>
                  <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
                  <span>{radio.category}</span>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); confirmRemove(radio.id, radio.stationName); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-rose-400 hover:bg-rose-50 hover:text-rose-600 transition-all cursor-pointer opacity-0 group-hover:opacity-100"
                title="Quitar de favoritos"
              >
                <i className="ri-heart-fill text-lg"></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}